import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import SeoHead from "../components/SeoHead";
import "./seo-keyword-page.css";

const BASE_URL = "https://www.oceanparadisehotel.us";
const HOTEL_NAME = "Ocean Paradise Hotel & Resort";
const ADDRESS = "773 Ocean Shores Blvd NW, Ocean Shores, WA 98569";
const PHONE = "+1 360-289-0664";
const BOOKING_URL = "https://bookingengine.stayflexi.com/?hotel_id=34243";

const keywordPages = {
  "/pet-friendly-hotel-ocean-shores": {
    title: "Pet Friendly Hotel in Ocean Shores WA | Ocean Paradise Hotel",
    h1: "Pet Friendly Hotel in Ocean Shores WA",
    description:
      "Ocean Paradise Hotel & Resort is a pet-friendly hotel in Ocean Shores WA near beach access with comfortable rooms, free parking, WiFi, indoor pool, and easy access to the Washington coast.",
    intro:
      "Traveling with a pet should not make your Ocean Shores trip harder. Ocean Paradise Hotel & Resort welcomes guests looking for a convenient pet-friendly hotel close to the beach, local attractions, and the main Ocean Shores travel corridor.",
    sections: [
      {
        heading: "Comfortable Stays for Pet Travelers",
        body:
          "Our location at 773 Ocean Shores Blvd NW keeps you close to beach access, restaurants, shops, and coastal activities while giving you the practical hotel amenities guests expect. Free parking, WiFi, comfortable rooms, and helpful service make the stay simple for families, couples, and travelers bringing a pet."
      },
      {
        heading: "Close to Ocean Shores Beach",
        body:
          "Ocean Shores is known for wide Pacific coast beaches, open-air walks, and relaxed weekend trips. Staying near the beach makes it easier to enjoy morning walks, sunset views, and quick returns to the hotel between outings."
      }
    ],
    faqs: [
      ["Is Ocean Paradise Hotel pet friendly?", "Ocean Paradise Hotel & Resort offers pet-friendly stays. Guests should contact the hotel directly for current pet room availability, fees, and policy details."],
      ["How close is the hotel to Ocean Shores beach?", "The property is near Ocean Shores beach access, making it convenient for guests planning a coastal stay."],
      ["Is parking available?", "Yes, guests can use hotel parking during their stay."]
    ]
  },
  "/hotel-with-pool-ocean-shores-wa": {
    title: "Hotel with Pool in Ocean Shores WA | Ocean Paradise Hotel",
    h1: "Hotel with Pool in Ocean Shores WA",
    description:
      "Book Ocean Paradise Hotel & Resort, a hotel with indoor pool and hot tub in Ocean Shores WA near beach access, free parking, WiFi, mini golf, and family-friendly rooms.",
    intro:
      "For many Ocean Shores visitors, a pool is one of the most important hotel amenities. Ocean Paradise Hotel & Resort gives guests a convenient place to relax after beach walks, sightseeing, clamming, road trips, and family activities.",
    sections: [
      {
        heading: "Indoor Pool and Hot Tub Convenience",
        body:
          "A hotel pool adds flexibility to a Washington coast trip, especially when weather changes. Guests can enjoy pool time, unwind after a day outside, and keep family travel simple without leaving the property."
      },
      {
        heading: "Beach Trip Plus Hotel Amenities",
        body:
          "The hotel combines Ocean Shores beach access with practical amenities including WiFi, free parking, comfortable rooms, and family-friendly features. It is a strong option for travelers who want both coastal access and on-site relaxation."
      }
    ],
    faqs: [
      ["Does Ocean Paradise Hotel have a pool?", "Yes, Ocean Paradise Hotel & Resort lists an indoor pool and hot tub among its guest amenities."],
      ["Is the hotel good for families?", "Yes, the hotel offers family-friendly rooms and amenities suitable for Ocean Shores trips."],
      ["Can I book direct?", "Yes, guests can book direct through the hotel booking link or contact the hotel by phone."]
    ]
  },
  "/cheap-hotel-ocean-shores-wa": {
    title: "Cheap Hotel in Ocean Shores WA | Ocean Paradise Hotel",
    h1: "Cheap Hotel in Ocean Shores WA",
    description:
      "Ocean Paradise Hotel & Resort is an affordable hotel in Ocean Shores WA near the beach with free WiFi, free parking, indoor pool, mini golf, pet-friendly options, and comfortable rooms.",
    intro:
      "Ocean Paradise Hotel & Resort is built for travelers who want value, location, and useful amenities in Ocean Shores. The hotel is a practical choice for beach weekends, family trips, road trips from Seattle or Portland, and budget-conscious Washington coast stays.",
    sections: [
      {
        heading: "Affordable Ocean Shores Location",
        body:
          "The hotel is located at 773 Ocean Shores Blvd NW, close to beach access and local attractions. Guests can stay near the coast without giving up essentials such as WiFi, parking, room comfort, and convenient booking."
      },
      {
        heading: "Value with Useful Amenities",
        body:
          "A cheap hotel should still be comfortable and easy to use. Ocean Paradise Hotel & Resort offers amenities that matter to travelers, including free parking, WiFi, indoor pool access, mini golf, and room options for different guest needs."
      }
    ],
    faqs: [
      ["Is Ocean Paradise Hotel an affordable hotel in Ocean Shores?", "Yes, Ocean Paradise Hotel & Resort targets value-focused travelers looking for affordable Ocean Shores lodging."],
      ["What amenities are included?", "Amenities include WiFi, free parking, indoor pool, hot tub, mini golf, and comfortable room options."],
      ["Where is the hotel located?", "The hotel is at 773 Ocean Shores Blvd NW, Ocean Shores, WA 98569."]
    ]
  },
  "/hotel-near-ocean-shores-beach": {
    title: "Hotel Near Ocean Shores Beach | Ocean Paradise Hotel",
    h1: "Hotel Near Ocean Shores Beach",
    description:
      "Stay at Ocean Paradise Hotel & Resort near Ocean Shores beach access with ocean coast convenience, free parking, WiFi, indoor pool, hot tub, and family-friendly rooms.",
    intro:
      "If beach access is the reason for your trip, location matters. Ocean Paradise Hotel & Resort puts guests close to the Ocean Shores coastline, making it easy to plan beach walks, family time, photos, sunsets, and Pacific coast activities.",
    sections: [
      {
        heading: "Close to the Pacific Coast",
        body:
          "Ocean Shores is a favorite Washington beach destination because of its wide shoreline, coastal scenery, and relaxed pace. Staying near the beach helps guests spend more time outside and less time driving between the hotel and the coast."
      },
      {
        heading: "A Practical Base for Ocean Shores",
        body:
          "The hotel also gives travelers easy access to local dining, attractions, and Ocean Shores Blvd. Guests can return to the hotel for parking, WiFi, indoor pool time, and comfortable rooms after exploring the beach."
      }
    ],
    faqs: [
      ["Is Ocean Paradise Hotel near Ocean Shores beach?", "Yes, the hotel is located near Ocean Shores beach access and is positioned for convenient coastal stays."],
      ["What is the hotel address?", `The hotel address is ${ADDRESS}.`],
      ["Does the hotel offer free parking?", "Yes, free parking is listed as a hotel amenity."]
    ]
  },
  "/ocean-shores-hotel-with-free-parking": {
    title: "Ocean Shores Hotel with Free Parking | Ocean Paradise Hotel",
    h1: "Ocean Shores Hotel with Free Parking",
    description:
      "Ocean Paradise Hotel & Resort offers hotel stays in Ocean Shores WA with free parking, beach access convenience, WiFi, indoor pool, hot tub, mini golf, and comfortable rooms.",
    intro:
      "Free parking makes an Ocean Shores trip easier, especially for guests arriving by car from Seattle, Tacoma, Olympia, Portland, or other Washington and Oregon cities. Ocean Paradise Hotel & Resort gives guests a convenient place to stay with parking included.",
    sections: [
      {
        heading: "Easy Arrival by Car",
        body:
          "Ocean Shores is a popular drive-to beach destination. Staying at a hotel with free parking helps guests avoid extra parking costs and keeps the trip simple when traveling with luggage, family, beach gear, or pets."
      },
      {
        heading: "Useful Amenities for Coastal Trips",
        body:
          "Beyond parking, guests can use WiFi, comfortable rooms, indoor pool and hot tub access, and other hotel amenities. The location near Ocean Shores beach access makes it a practical choice for weekend stays and longer coastal visits."
      }
    ],
    faqs: [
      ["Does Ocean Paradise Hotel offer free parking?", "Yes, free parking is listed as an amenity for hotel guests."],
      ["Is the hotel good for road trips?", "Yes, the Ocean Shores Blvd NW location works well for guests driving to the Washington coast."],
      ["Can I get directions?", "Yes, guests can use the contact page for the hotel map and directions link."]
    ]
  }
};

function buildJsonLd(page, path) {
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: {
        "@type": "Answer",
        text: answer
      }
    }))
  };

  const hotelJsonLd = {
    "@context": "https://schema.org",
    "@type": "Hotel",
    name: HOTEL_NAME,
    url: `${BASE_URL}${path}`,
    telephone: PHONE,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: "773 Ocean Shores Blvd NW",
      addressLocality: "Ocean Shores",
      addressRegion: "WA",
      postalCode: "98569",
      addressCountry: "US"
    },
    amenityFeature: [
      { "@type": "LocationFeatureSpecification", name: "Free parking", value: true },
      { "@type": "LocationFeatureSpecification", name: "Free WiFi", value: true },
      { "@type": "LocationFeatureSpecification", name: "Indoor pool", value: true },
      { "@type": "LocationFeatureSpecification", name: "Hot tub", value: true },
      { "@type": "LocationFeatureSpecification", name: "Pet-friendly rooms", value: true }
    ],
    potentialAction: {
      "@type": "ReserveAction",
      target: BOOKING_URL
    }
  };

  return [hotelJsonLd, faqJsonLd];
}

export default function SeoKeywordPage() {
  const { pathname } = useLocation();
  const page = keywordPages[pathname];

  if (!page) {
    return <Navigate to="/" replace />;
  }

  return (
    <main className="seo-keyword-page">
      <SeoHead
        title={page.title}
        description={page.description}
        canonical={pathname}
        jsonLd={buildJsonLd(page, pathname)}
      />

      <article className="seo-keyword-article">
        <p className="seo-keyword-eyebrow">Ocean Shores WA Hotel</p>
        <h1>{page.h1}</h1>
        <p className="seo-keyword-intro">{page.intro}</p>

        <div className="seo-keyword-details">
          <span>{HOTEL_NAME}</span>
          <span>{ADDRESS}</span>
          <span>{PHONE}</span>
        </div>

        {page.sections.map((section) => (
          <section key={section.heading}>
            <h2>{section.heading}</h2>
            <p>{section.body}</p>
          </section>
        ))}

        <section>
          <h2>Why Book Ocean Paradise Hotel & Resort</h2>
          <p>
            Guests choose Ocean Paradise Hotel & Resort for its Ocean Shores location,
            beach access convenience, room options, free parking, WiFi, indoor pool,
            hot tub, mini golf, and easy direct booking. The hotel is a useful base for
            family vacations, pet-friendly trips, beach weekends, and affordable stays
            on the Washington coast.
          </p>
        </section>

        <section>
          <h2>Frequently Asked Questions</h2>
          {page.faqs.map(([question, answer]) => (
            <div className="seo-keyword-faq" key={question}>
              <h3>{question}</h3>
              <p>{answer}</p>
            </div>
          ))}
        </section>

        <div className="seo-keyword-actions">
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">Book Direct</a>
          <Link to="/contact">View Map & Directions</Link>
        </div>
      </article>
    </main>
  );
}
